/**
 * ไฟล์โอน K-Cash จากชีตเงินเดือน — เมนู เงินเดือน > ไฟล์โอน K-Cash (จากชีต)
 *
 * บริษัทที่คิดเงินเดือนในชีตเอง (TN Riches): export ชีตที่คิดเสร็จเป็น CSV → อัปโหลด →
 * ดูตัวอย่าง (ใครเข้าไฟล์ ใครต้องโอนแยก เพราะอะไร) → ดาวน์โหลดไฟล์ .txt ไปอัปโหลดเข้า K-Cash
 * ยอดโอน = ช่อง "รวมรับสุทธิ" ในชีต · ตรรกะอยู่ที่ worker lib/kcash-from-sheet.ts
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, Banknote, AlertTriangle, CheckCircle2, FileSpreadsheet, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { StatCard } from '../../components/ui/StatCard';
import { useToast } from '../../components/ui/toast';
import { apiFetch } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';

interface OkRow { row: number; seq: string; emp_code: string; name: string; account_no: string; amount: number }
interface ManualRow { row: number; seq: string; emp_code: string | null; name: string; amount: number; reason: string; bank?: string | null }
interface Preview {
  need_month?: boolean;
  month: string;
  months: string[];
  sheet_rows: number;
  sheet_total: number;
  ok_count: number;
  ok_total: number;
  manual_count: number;
  manual_total: number;
  company_account_set: boolean;
  blocking: string[];
  skipped: Array<{ row: number; name: string; net: number }>;
  plan_hash: string;
  ok: OkRow[];
  manual: ManualRow[];
}
interface FileResult { filename: string; file_base64: string; ok_count: number; ok_total: number }

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayBkk = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);

/** Google Sheets export = UTF-8 · Excel "CSV" บน Windows ไทย = windows-874 → ลองทีละแบบ */
async function readCsvText(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-874').decode(buf);
  }
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const csvCell = (v: string | number) => {
  const s = String(v);
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export function KcashFromSheet() {
  const toast = useToast();
  const role = useAuthStore((s) => s.profile?.role);
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState('');
  const [transferDate, setTransferDate] = useState(todayBkk());
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState<'' | 'read' | 'preview' | 'file' | 'manual'>('');
  // ตัวนับคำขอ — อัปโหลดไฟล์ใหม่ทับระหว่างรอ ผลของไฟล์เก่าที่ตอบช้ากว่าต้องถูกทิ้ง
  const reqId = useRef(0);
  // ไฟล์+เดือนที่ได้ผลตรวจชุดนี้ — ดาวน์โหลดต้องส่งชุดเดียวกับที่เห็นเสมอ
  const [previewInput, setPreviewInput] = useState<{ csv: string; month: string } | null>(null);

  async function runPreview(text: string, m: string) {
    const id = ++reqId.current;
    setBusy('preview');
    setPreview(null);
    setPreviewInput(null);
    try {
      const res = await apiFetch<Preview>('/owner/payroll/kcash-from-sheet', {
        method: 'POST',
        body: { csv_text: text, month: m || undefined, mode: 'preview' },
      });
      if (id !== reqId.current) return;
      if (res.need_month) {
        setMonths(res.months);
        toast.info('ไฟล์นี้มีหลายเดือน — เลือกเดือนที่จะโอน');
        return;
      }
      setMonths(res.months);
      setMonth(res.month);
      setPreview(res);
      setPreviewInput({ csv: text, month: res.month });
    } catch (e) {
      if (id === reqId.current) toast.error(e instanceof Error ? e.message : 'ตรวจไฟล์ไม่สำเร็จ');
    } finally {
      if (id === reqId.current) setBusy('');
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const id = ++reqId.current;
    setBusy('read');
    setPreview(null);
    setPreviewInput(null);
    let text: string;
    try {
      text = await readCsvText(f);
    } catch {
      if (id === reqId.current) { setBusy(''); toast.error('อ่านไฟล์ไม่ได้'); }
      return;
    }
    if (id !== reqId.current) return;
    setFileName(f.name);
    setCsvText(text);
    setMonths([]);
    setMonth('');
    await runPreview(text, '');
  }

  async function handleDownload() {
    if (!preview || !previewInput || busy) return;
    setBusy('file');
    try {
      const res = await apiFetch<FileResult>('/owner/payroll/kcash-from-sheet', {
        method: 'POST',
        body: {
          csv_text: previewInput.csv,
          month: previewInput.month || undefined,
          transfer_date: transferDate,
          plan_hash: preview.plan_hash,
          mode: 'file',
        },
      });
      const bin = atob(res.file_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      saveBlob(new Blob([bytes], { type: 'text/plain' }), res.filename);
      toast.success(`ดาวน์โหลดไฟล์ K-Cash แล้ว ${res.ok_count} คน ยอด ${baht(res.ok_total)} บาท`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างไฟล์ไม่สำเร็จ');
    } finally {
      setBusy('');
    }
  }

  // fb#69 — รายชื่อโอนแยกต้องมีธนาคาร + เลขบัญชีเต็ม (บัญชีต่างธนาคารโอนผ่าน K BIZ/K PLUS) จึงขอจากเซิร์ฟเวอร์
  // (ถอดรหัสเลขบัญชี + audit เหมือนไฟล์โอน) — ถ้าเซิร์ฟเวอร์ตอบไม่ได้ ตกกลับ CSV จากตัวอย่างบนจอ (ไม่มีเลขบัญชี)
  async function downloadManual() {
    if (!preview || !previewInput || busy) return;
    setBusy('manual');
    try {
      const jwt = useAuthStore.getState().jwt;
      const apiBase = import.meta.env.VITE_API_BASE ?? '';
      const res = await fetch(`${apiBase}/owner/payroll/kcash-from-sheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify({ csv_text: previewInput.csv, month: previewInput.month || undefined, plan_hash: preview.plan_hash, mode: 'manual_csv' }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* ไม่ใช่ JSON */ }
        throw new Error(msg);
      }
      saveBlob(await res.blob(), `ต้องโอนแยก-${transferDate}.csv`);
      toast.success(`ดาวน์โหลดรายชื่อโอนแยกแล้ว ${preview.manual_count} คน (มีธนาคาร + เลขบัญชี)`);
    } catch (e) {
      toast.error(`ดาวน์โหลดรายชื่อโอนแยกไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'} — ใช้รายการบนจอแทน (ไม่มีเลขบัญชี)`);
      const lines = ['ลำดับในชีต,รหัสพนักงาน,ชื่อ,ธนาคาร,ยอดโอน,เหตุผล',
        ...preview.manual.map((m) => [m.seq, m.emp_code ?? '', m.name, m.bank ?? '', m.amount.toFixed(2), m.reason].map(csvCell).join(','))];
      saveBlob(new Blob(['﻿' + lines.join('\n')], { type: 'text/csv' }), `ต้องโอนแยก-${transferDate}.csv`);
    } finally {
      setBusy('');
    }
  }

  const canDownload = !!preview && !!previewInput && preview.blocking.length === 0 && preview.company_account_set && preview.ok_count > 0 && !!transferDate;

  return (
    <AdminLayout
      title="ไฟล์โอน K-Cash (จากชีต)"
      subtitle="อัปโหลดชีตเงินเดือนที่คิดเสร็จแล้ว (CSV) — ระบบออกไฟล์ .txt สำหรับอัปโหลดเข้า K-Cash ของกสิกรไทย"
    >
      <div className="card p-4 mb-4 space-y-3">
        <ol className="text-sm text-gray-600 list-decimal ml-5 space-y-1">
          <li>ดาวน์โหลด "ไฟล์สำหรับชีตเงินเดือน" จากหน้า <Link to="/owner/cycle#reconcile" className="text-brand-700 underline">สรุปเวลาเข้างาน</Link> แล้วคิดเงินต่อในชีตตามปกติ</li>
          <li>ในชีต เลือก ไฟล์ → ดาวน์โหลด → CSV (ชีตเดิมของบริษัทก็ใช้ได้ ระบบจับคนด้วยเลขบัตรประชาชน)</li>
          <li>อัปโหลดที่นี่ ตรวจรายชื่อ แล้วกดดาวน์โหลดไฟล์ K-Cash</li>
        </ol>
        <p className="text-xs text-gray-500">ยอดโอน = ช่อง "รวมรับสุทธิ" ในชีต · K-Cash โอนได้เฉพาะบัญชีกสิกรไทย คนอื่นระบบแยกรายชื่อให้โอนเอง</p>
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
          หน้านี้สำหรับผู้บริหารเท่านั้น — ยอดมาจากไฟล์ที่อัปโหลด ไม่ผ่านขั้นอนุมัติในระบบ ·
          ถ้าคิดเงินเดือนในระบบ ฝ่ายบัญชีดาวน์โหลดไฟล์โอนได้ที่ รอบเงินเดือน &gt; ปิดเดือน หลังผู้บริหารอนุมัติและปิดงวดแล้ว
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="btn-primary cursor-pointer">
            <Upload className="w-4 h-4" /> {busy === 'read' || busy === 'preview' ? 'กำลังตรวจไฟล์…' : 'อัปโหลดชีต (CSV)'}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => void handleFile(e)} disabled={!!busy} />
          </label>
          {fileName && <span className="text-sm text-gray-600 flex items-center gap-1 min-w-0"><FileSpreadsheet className="w-4 h-4 shrink-0" /><span className="truncate">{fileName}</span></span>}
        </div>

        {months.length > 1 && (
          <div>
            <label className="label">เดือนที่จะโอน</label>
            <select
              className="input"
              value={month}
              onChange={(e) => { setMonth(e.target.value); if (e.target.value) void runPreview(csvText, e.target.value); }}
              disabled={!!busy}
            >
              <option value="">— เลือกเดือน —</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>

      {preview && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <StatCard icon={FileSpreadsheet} label={`ในชีต${preview.month ? ` (${preview.month})` : ''}`} value={`${baht(preview.sheet_total)} ฿`} hint={`${preview.sheet_rows} คน`} />
            <StatCard icon={Banknote} label="เข้าไฟล์ K-Cash" value={`${baht(preview.ok_total)} ฿`} hint={`${preview.ok_count} คน`} tone="success" />
            <StatCard icon={Users} label="ต้องโอนแยก" value={`${baht(preview.manual_total)} ฿`} hint={`${preview.manual_count} คน`} tone={preview.manual_count > 0 ? 'warn' : 'default'} />
          </div>

          {preview.blocking.length > 0 && (
            <div className="card p-3 mb-4 border-red-200 bg-red-50 text-sm text-red-700">
              <div className="font-medium flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> ต้องแก้ในชีตก่อนออกไฟล์</div>
              <ul className="list-disc ml-5">{preview.blocking.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          )}
          {preview.skipped.length > 0 && (
            <p className="text-xs text-gray-500 mb-4">
              ข้ามแถวยอดรวมในชีต (ไม่ใช่พนักงาน): {preview.skipped.map((s) => `แถว ${s.row} "${s.name}" ${baht(s.net)}`).join(' · ')}
            </p>
          )}
          {!preview.company_account_set && (
            <div className="card p-3 mb-4 border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                ยังไม่ได้ตั้งเลขบัญชีบริษัท (บัญชีกสิกร 10 หลักที่ใช้ตัดจ่าย) —{' '}
                {role === 'OWNER'
                  ? <Link to="/owner/settings#payroll" className="underline">ตั้งที่ ตั้งค่าองค์กร &gt; รอบเงินเดือน &gt; ไฟล์โอนเงินเดือน</Link>
                  : 'ให้เจ้าของตั้งที่ ตั้งค่าองค์กร > รอบเงินเดือน > ไฟล์โอนเงินเดือน'}
              </span>
            </div>
          )}

          <div className="card p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="label">วันที่โอน</label>
                <input type="date" className="input" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-2 ml-auto">
                {preview.manual_count > 0 && (
                  <button onClick={() => void downloadManual()} disabled={!!busy} className="btn-secondary">
                    <Download className="w-4 h-4" /> {busy === 'manual' ? 'กำลังสร้าง…' : 'รายชื่อที่ต้องโอนแยก (CSV มีเลขบัญชี)'}
                  </button>
                )}
                <button onClick={() => void handleDownload()} disabled={!canDownload || !!busy} className="btn-primary">
                  <Download className="w-4 h-4" /> {busy === 'file' ? 'กำลังสร้างไฟล์…' : `ดาวน์โหลดไฟล์ K-Cash (${preview.ok_count} คน)`}
                </button>
              </div>
            </div>
          </div>

          {preview.manual.length > 0 && (
            <div className="card p-4 mb-4">
              <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> ต้องโอนแยก ({preview.manual.length} คน)</div>
              <p className="text-xs text-gray-600 mb-2">
                ไฟล์ K-Cash โอนได้เฉพาะบัญชีกสิกรไทย — คนที่ใช้ธนาคารอื่น (เช่น SCB) ให้ดาวน์โหลด "รายชื่อที่ต้องโอนแยก" (มีธนาคาร + เลขบัญชี + ยอด)
                แล้วโอนต่างธนาคารผ่าน K BIZ / K PLUS · ทางเลือกอื่น: ตั้งค่าธนาคารหลักเป็น SCB เพื่อออกไฟล์ SCB Business Anywhere หรือ CSV ทั่วไป ที่ ตั้งค่าองค์กร &gt; ไฟล์โอนเงินเดือน
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr><th className="px-2 py-1.5 text-left">ลำดับ</th><th className="px-2 py-1.5 text-left">ชื่อ</th><th className="px-2 py-1.5 text-left">ธนาคาร</th><th className="px-2 py-1.5 text-right">ยอดโอน</th><th className="px-2 py-1.5 text-left">เหตุผล</th></tr>
                  </thead>
                  <tbody>
                    {preview.manual.map((m) => (
                      <tr key={`${m.row}`} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 whitespace-nowrap">{m.seq}</td>
                        <td className="px-2 py-1.5">{m.name}{m.emp_code && <span className="text-gray-400"> · {m.emp_code}</span>}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{m.bank ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{baht(m.amount)}</td>
                        <td className="px-2 py-1.5 text-amber-700">{m.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.ok.length > 0 && (
            <div className="card p-4">
              <div className="font-semibold mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-600" /> อยู่ในไฟล์ K-Cash ({preview.ok.length} คน)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr><th className="px-2 py-1.5 text-left">ลำดับ</th><th className="px-2 py-1.5 text-left">ชื่อบัญชี</th><th className="px-2 py-1.5 text-left">เลขบัญชี</th><th className="px-2 py-1.5 text-right">ยอดโอน</th></tr>
                  </thead>
                  <tbody>
                    {preview.ok.map((r) => (
                      <tr key={r.emp_code} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.seq}</td>
                        <td className="px-2 py-1.5">{r.name}<span className="text-gray-400"> · {r.emp_code}</span></td>
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.account_no}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{baht(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
